<template>
  <div id="page-recu">
    <div id="recu-print"  ref="recu" class="recu-card">
      <!-- En-tête -->
      <div class="recu-header d-flex align-items-center mb-4 p-3 rounded-top">
        <img
          v-if="logoUrl"
          :src="logoUrl"
          alt="Logo Clinique"
          class="logo"
        />
        <div class="flex-grow-1 text-center">
          <h2 class="fw-bold mb-1 text-white">{{ systeme?.nom ?? 'CLINIQUE' }}</h2>
          <small class="text-light d-block">
            {{ [systeme?.adresse, systeme?.telephone].filter(Boolean).join(' • ') }}
          </small>
        </div>
      </div>

      <!-- Titre et Référence -->
      <div class="d-flex justify-content-between align-items-center mb-4 px-3">
        <h4 class="text-primary fw-bold mb-0">Reçu de Consultation</h4>
        <small class="text-muted">Réf : {{ reference }}</small>
      </div>

      <!-- Infos patient et consultation -->
      <div class="row g-4 mb-3 px-3">
        <div class="col-md-6 info-card">
          <h5 class="card-title">Patient</h5>
          <p class="mb-1"><strong>N° consultation :</strong> {{ consultation?.numConsultation }}</p>
          <p class="mb-1"><strong>Nom :</strong> {{ consultation.patient?.prenom }} {{ consultation.patient?.nom }}</p>
          <p class="mb-1"><strong>Téléphone :</strong> {{ consultation.patient?.telephone || consultation.patient?.telephoneContact }}</p>
          <p class="mb-1" v-if="responsableTexte"><strong>Responsable :</strong> {{ responsableTexte }}</p>
          <p class="mb-0"><strong>Date :</strong> {{ formatDate(consultation?.createdAt) }}</p>
        </div>
        <div class="col-md-6 info-card">
          <h5 class="card-title">Consultation</h5>
          <p class="mb-1"><strong>Type :</strong> {{ consultation.type?.nom }}</p>
          <p class="mb-1"><strong>Assurance :</strong> {{ consultation.assurancePaiement }}</p>
          <p class="mb-0"><strong>Montant :</strong> {{ consultation.type?.prix }} FCFA</p>
        </div>
      </div>

      <!-- Statut paiement et Agent -->
      <div class="d-flex justify-content-between align-items-center mb-4 px-3">
        <span class="badge" :class="consultation.estPaye === 'Payé' ? 'badge-success' : 'badge-danger'">
          {{ consultation.estPaye }}
        </span>
        <span><strong>Agent :</strong> <span class="text-muted">{{ consultation.createdBy?.prenom }} {{ consultation.createdBy?.nom }}</span></span>
      </div>

      <hr class="divider" />

      <!-- QR Code -->
      <div class="text-center my-4">
        <qrcode-vue :value="String(consultation.patient?.numeroDossier ?? consultation.id ?? '')" :size="140" />
        <p class="text-muted mt-2">ID Consultation : {{ consultation.id }}</p>
      </div>

      <!-- Boutons (masqués à l'impression) -->
      <div class="text-end mt-4 d-print-none px-3">
        <button class="btn btn-outline-primary me-2" @click="imprimer('A5')">
          <i class="bi bi-printer me-1"></i> Imprimer (A5)
        </button>
        <button class="btn btn-outline-secondary me-2" @click="imprimer('ticket80')">
          <i class="bi bi-receipt me-1"></i> Ticket 80 mm
        </button>
        <button class="btn btn-outline-secondary me-2" @click="imprimer('ticket58')">
          <i class="bi bi-receipt-cutoff me-1"></i> Ticket 58 mm
        </button>
        <button class="btn btn-outline-success" @click="telechargerPDF">
          <i class="bi bi-download me-1"></i> Télécharger
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import dayjs from 'dayjs';
import QRCodeVue from 'qrcode.vue';
import { imprimer, telechargerPdf, configClinique, urlFichier, e, fcfa, dateFr, blocInfos, nomComplet, estTicket, choisirFormatTicket } from '@/impression/impression';

export default {
  components: { 'qrcode-vue': QRCodeVue },
  data() {
    return {
      consultation: {},
      reference: '',
      systeme: null,
    };
  },
  computed: {
    logoUrl() {
      return urlFichier(this.systeme?.logo);
    },
    /** Mineur : son responsable légal principal figure sur le reçu. */
    responsableTexte() {
      const p = this.consultation?.patient || {};
      if (!p.mineur) return '';
      const r = (p.responsablesListe || []).find(x => x.principal) || (p.responsablesListe || [])[0];
      return r ? `${r.lienLibelle} : ${r.nomComplet}${r.telephone ? ' (' + r.telephone + ')' : ''}` : '';
    },
  },
  created() {
    this.getConsultation();
    this.getConfigurationSysteme();
  },
  methods: {
    formatDate(date) {
      return dayjs(date).format('DD-MM-YYYY HH:mm');
    },
    async getConsultation() {
      try {
        const { id } = this.$route.params;
        const res = await api.getItem('consultations', id);
        this.consultation = res.data;
        this.reference = `CON-${dayjs().format('DD')}-${id.toString().padStart(5, '0')}`;
      } catch (e) {
        console.error('Erreur chargement consultation', e);
      }
    },
    async getConfigurationSysteme() {
      this.systeme = await configClinique();
    },

    // Options communes à l'impression et au PDF (mise en page partagée)
    optionsDocument(format = 'A5') {
      const consultation = this.consultation || {};
      const patient = consultation.patient || {};
      const agent = nomComplet(consultation.createdBy);
      const date = dateFr(consultation.createdAt, true);

      let titre = 'Document de consultation';
      if (consultation.statut === 'En attente') {
        titre = 'Bon de consultation';
      } else if (consultation.statut === 'Terminée' && consultation.estPaye === 'Non payé') {
        titre = 'Facture de consultation';
      } else if (consultation.statut === 'Terminée' && consultation.estPaye === 'Payé') {
        titre = 'Reçu de consultation';
      }

      if (estTicket(format)) {
        return {
          titre,
          numero: consultation.numConsultation || this.reference,
          date,
          format,
          corps: `
            ${consultation.numeroFile ? `<div class="centre" style="margin:2px 0 6px"><div>N° de passage</div><div class="grand-numero">${e(consultation.numeroFile)}</div></div>` : ''}
            ${blocInfos([
              ['Patient', nomComplet(patient)],
              ['Dossier', patient.numeroDossier || '—'],
              ...(this.responsableTexte ? [['Responsable', this.responsableTexte]] : []),
              ['Consultation', consultation.type?.nom || '—'],
              ['Assurance', consultation.assurancePaiement || 'Aucune'],
            ], 1)}
            <table class="totaux">
              <tr class="grand"><td>Montant</td><td class="droite">${e(fcfa(consultation.type?.prix))}</td></tr>
              <tr><td>Paiement</td><td class="droite">${e(consultation.estPaye || 'Non payé')}</td></tr>
            </table>
            <div class="merci">${agent ? `Agent : ${e(agent)}<br>` : ''}Merci de votre confiance.</div>`,
        };
      }

      const corps = `
        ${blocInfos([
          ['Patient', nomComplet(patient)],
          ['N° dossier', patient.numeroDossier || '—'],
          ...(this.responsableTexte ? [['Responsable', this.responsableTexte]] : []),
          ['Téléphone', patient.telephone || patient.telephoneContact],
          ['N° consultation', consultation.numConsultation || '—'],
          ['Date', date],
          ['Statut', consultation.statut || '—'],
        ])}
        <table class="lignes">
          <thead><tr><th>Type de consultation</th><th>Assurance</th><th class="droite">Montant</th><th class="centre">Paiement</th></tr></thead>
          <tbody><tr>
            <td>${e(consultation.type?.nom || '—')}</td>
            <td>${e(consultation.assurancePaiement || 'Aucune')}</td>
            <td class="droite">${e(fcfa(consultation.type?.prix))}</td>
            <td class="centre">${consultation.estPaye === 'Payé' ? '<span class="badge-ok">Payé</span>' : `<span class="badge-ko">${e(consultation.estPaye || 'Non payé')}</span>`}</td>
          </tr></tbody>
        </table>`;

      return {
        titre,
        numero: consultation.numConsultation || this.reference,
        date,
        corps,
        format: 'A5',
        signature: { droite: `<strong>L’agent</strong>${agent ? '<br>' + e(agent) : ''}<br><span class="muted">Signature et cachet</span>` },
      };
    },

    async imprimer(format = 'A5') {
      if (estTicket(format)) choisirFormatTicket(format);
      await imprimer(this.optionsDocument(format));
    },

    async telechargerPDF() {
      const c = this.consultation || {};
      await telechargerPdf(this.optionsDocument(), `recu-${c.numConsultation || this.reference || c.id}.pdf`);
    },
  }
};
</script>

<style scoped>
.recu-card {
  background: #fff;
  width: 100%;
  min-height: 100vh;
  padding: 30px;
  box-sizing: border-box;
  font-family: 'Segoe UI', sans-serif;
}

/* En-tête */
.recu-header {
  background: linear-gradient(90deg, #0d6efd, #6610f2);
  color: #fff;
}
.logo {
  height: 70px;
  width: 70px;
  object-fit: cover;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}

/* Infos patient / consultation */
.info-card {
  background: #f9f9f9;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 10px rgba(0,0,0,0.05);
  border-left: 5px solid #0d6efd;
}
.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #0d6efd;
  margin-bottom: 10px;
}

/* Badge statut */
.badge {
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 25px;
}
.badge-success { background-color: #28a745; color: #fff; }
.badge-danger { background-color: #dc3545; color: #fff; }

/* Séparateur */
.divider { border: 0; border-top: 1px dashed #ccc; }
</style>

